import { useI18n }               from '../../../../shared/i18n/useI18n.js'

import { formatPriceLabelShort } from '../../../../shared/utils/priceUtils.js'

import { 
  areFilterConfigsEqual,
  isFilterActive }                from '../../utils/filterUtils.js'
import { getMultiSelectOptions }  from '../../utils/filterFormatters.js'
import { 
  FILTER_IDS, 
  COMPONENT_TYPES }               from '../../constants/filterConstants.js'

import { RangeSlider } from '../../../../shared/ui/RangeSlider/index.js'
import { RangeInput }  from '../../../../shared/ui/RangeInput/index.js'
import { MultiSelect } from '../../../../shared/ui/MultiSelect/index.js'
import { Input }       from '../../../../shared/ui/Input/index.js'
import { Search }      from '../../../../shared/ui/icons/index.js'

/**
 * Renders the appropriate input primitive for a given filter registry entry.
 * Shared by CatalogAdditionalPanel (desktop) and CatalogFiltersSheet (mobile).
 *
 * @param {object} props
 * @param {object} props.entry            - filterRegistry entry
 * @param {*}      props.value            - current value from filtersConfig[entry.id]
 * @param {object} props.range            - full range from fullFiltersRange[entry.id]
 * @param {boolean} props.isActive        - drives isActive prop on inputs
 * @param {(value: *) => void} props.onChange - filtered update (already keyed by id)
 */
export function FilterInput({ entry, value, range, isActive, onChange }) {
  const { t } = useI18n()
  const id = entry.id

  // ── TextSearch ────────────────────────────────────────────────────────────
  if (entry.component === COMPONENT_TYPES.TEXT_SEARCH) {
    return (
      <Input
        value={value ?? ''}
        onChange={onChange}
        placeholder={entry.placeholderKey ? t(entry.placeholderKey) : t('catalog.search_placeholder')}
        isActive={isActive}
        suffix={<Search />}
        trimOnBlur
      />
    )
  }

  // ── RangeSlider ───────────────────────────────────────────────────────────
  if (entry.component === COMPONENT_TYPES.RANGE_SLIDER) {
    const rangeArr = Array.isArray(range) ? range : [0, 1]
    return (
      <RangeSlider
        defaultValue={rangeArr}
        value={Array.isArray(value) ? value : rangeArr}
        onChange={onChange}
        isActive={isActive}
        step={entry.step}
        formatValue={
          id === FILTER_IDS.PRICE_RANGE ? formatPriceLabelShort :
          id === FILTER_IDS.AREA        ? v => `${v} m²` :
          undefined
        }
      />
    )
  }

  // ── RangeInputs ─────────────────────────────────────────────────────────────────────────────
  if (entry.component === COMPONENT_TYPES.RANGE_INPUTS) {
    const rangeArr = Array.isArray(range) ? range : [0, 1]
    return (
      <RangeInput
        defaultValue={rangeArr}
        value={Array.isArray(value) ? value : rangeArr}
        onChange={onChange}
        step={entry.step}
        isActive={isActive}
        suffix={id === FILTER_IDS.PRICE_INPUT ? 'zł' : undefined}
      />
    )
  }

  // ── MultiSelect — strict-match shape { selected, strict } ────────────────
  if (
    entry.component === COMPONENT_TYPES.MULTI_SELECT &&
    !Array.isArray(range) && Array.isArray(range?.selected)
  ) {
    const options = getMultiSelectOptions(entry, { [id]: range })
    return (
      <MultiSelect
        value={value?.selected ?? []}
        onChange={v => onChange({ ...(value ?? {}), selected: v })}
        options={options}
        isActive={isActive}
        variant="summary"
        label={entry.labelKey ? t(entry.labelKey) : undefined}
        strictValue={value?.strict ?? false}
        onStrictChange={v => onChange({ ...(value ?? {}), strict: v })}
        strictTooltip={entry.strictTooltipKey ? t(entry.strictTooltipKey) : undefined}
        infoTooltip={entry.infoTooltipKey ? t(entry.infoTooltipKey) : undefined}
      />
    )
  }

  // ── MultiSelect — standard array ─────────────────────────────────────────
  if (entry.component === COMPONENT_TYPES.MULTI_SELECT) {
    const options = getMultiSelectOptions(entry, { [id]: range })
    return (
      <MultiSelect
        value={Array.isArray(value) ? value.map(String) : []}
        onChange={onChange}
        options={options}
        isActive={isActive}
        placeholder={entry.placeholderKey ? t(entry.placeholderKey) : undefined}
        variant="summary"
        label={entry.labelKey ? t(entry.labelKey) : undefined}
        infoTooltip={entry.infoTooltipKey ? t(entry.infoTooltipKey) : undefined}
      />
    )
  }

  // Fallback — unknown component type
  return null
}
